import { isEqual } from 'lodash-es';
import pDebounce from 'p-debounce';
import type { ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import AsyncSelectComponent from 'react-select/async';

import { SEARCH_DEBOUNCE_DELAY } from '@zen/utils/constants';
import { useHover } from '@zen/utils/hooks/useHover';
import type { Nullable, Undefinable } from '@zen/utils/typescript';

import type { LabelPlacement } from '../../Label';
import Label from '../../Label';
import { ClearIndicator, getMenuList, getNoOptionsMessage, Option as OptionComponent, ValueContainer } from '../components';
import DropdownIndicator from '../components/DropdownIndicator';
import LoadingMessage from '../components/LoadingMessage';
import { asyncSelectStyles, customTheme } from '../select-styles';
import type { CommonProps, SelectVariant } from '../types';

interface Props<Value> extends CommonProps {
  cacheOptions?: boolean;
  defaultOptions?: boolean | Value[];
  formatOptionLabel: (value: Value, inputValue: string, isSelected: boolean, context: 'menu' | 'value') => ReactNode;
  isOptionDisabled?: (option: Value) => boolean;
  label?: ReactNode;
  labelPlacement?: LabelPlacement;
  loadOptions: (inputValue: string) => Promise<Value[]>;
  noOptionsMessage?: ReactNode;
  onChange?: (value: Nullable<Value>) => void;
  onInputChange?: (inputValue: string) => void;
  optionKey?: keyof Value;
  renderMenuInPortal?: boolean;
  size?: 'default' | 'compact';
  value?: Nullable<Value>;
  variant?: SelectVariant;
}

const AsyncSelect = <ResultType extends {}>(props: Props<ResultType>) => {
  const {
    autoFocus,
    cacheOptions = true,
    defaultOptions,
    dropdownFooterRenderer,
    noOptionsMessage,
    className,
    hasError,
    inputValue,
    isClearable,
    isDisabled,
    isLoading,
    isOptionDisabled,
    label,
    isSearchable = true,
    labelPlacement,
    loadOptions,
    name,
    onBlur,
    onChange,
    onInputChange,
    optionKey,
    placeholder = 'Search...',
    renderMenuInPortal = false,
    size = 'default',
    suggestion,
    variant = 'default',
    value
  } = props;

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [ref, isHovered] = useHover();

  const onInputValueChange = (input: string): void => {
    setIsMenuOpen(input.length > 0);

    onInputChange?.(input);
  };

  const handleFocus = (): void => setIsMenuOpen(true);

  const debouncedHandleInputChange = pDebounce(loadOptions, SEARCH_DEBOUNCE_DELAY);

  const formatLabel = (
    option: ResultType,
    {
      context,
      inputValue: input,
      selectValue
    }: { context: 'menu' | 'value'; inputValue: string; selectValue: readonly ResultType[] }
  ): ReactNode => {
    const isSelected = isOptionSelected(option, selectValue);

    return props.formatOptionLabel(option, input, isSelected, context);
  };

  const isOptionSelected = (selectedOption: ResultType, options: readonly ResultType[]) => {
    return !!options.find((option) => {
      if (optionKey) {
        return selectedOption[optionKey] === option[optionKey];
      }

      return isEqual(selectedOption, option);
    });
  };

  const shouldEnableBlurOnSelect: boolean = variant !== 'default';
  const focusHandler: Undefinable<() => void> = defaultOptions ? handleFocus : undefined;
  const asyncSelect: ReactElement = (
    <div ref={ref} className="flex-1">
      <AsyncSelectComponent<ResultType>
        autoFocus={autoFocus}
        blurInputOnSelect={shouldEnableBlurOnSelect}
        cacheOptions={cacheOptions}
        className={className}
        classNames={{ option: () => 'async-select-option' }}
        closeMenuOnSelect={true}
        components={{
          DropdownIndicator,
          ...(variant === 'default' ? { ValueContainer } : {}),
          MenuList: getMenuList(dropdownFooterRenderer),
          ...(!noOptionsMessage ? { NoOptionsMessage: getNoOptionsMessage(suggestion, true) } : {}),
          ClearIndicator,
          Option: OptionComponent
        }}
        defaultOptions={defaultOptions}
        formatOptionLabel={formatLabel}
        inputId={name}
        inputValue={inputValue}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isMulti={false}
        isOptionDisabled={isOptionDisabled}
        isOptionSelected={isOptionSelected}
        isSearchable={isSearchable}
        loadingMessage={LoadingMessage}
        loadOptions={debouncedHandleInputChange}
        menuIsOpen={isMenuOpen}
        menuPlacement="auto"
        name={name}
        noOptionsMessage={() => noOptionsMessage}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={focusHandler}
        onInputChange={onInputValueChange}
        placeholder={placeholder}
        styles={asyncSelectStyles({ hasError, variant, isHovered, isCompactSize: size === 'compact' })}
        theme={customTheme}
        value={value}
        {...(renderMenuInPortal ? { menuPortalTarget: document.body } : {})}
      />
    </div>
  );

  if (!label) {
    return asyncSelect;
  }

  return (
    <Label content={label} labelPlacement={labelPlacement} name={name || 'select'} variant={variant}>
      {asyncSelect}
    </Label>
  );
};

export type { Props as AsyncSelectProps };

export default AsyncSelect;
